import { useState, useEffect } from 'react';
import { withWindow } from '@shared/utils/withWindow';

/**
 * Media query for detecting touch devices.
 *
 * `pointer: coarse` matches devices where the primary pointing device has limited
 * accuracy (e.g., touchscreens). This is more reliable than user-agent sniffing
 * because it detects the actual input capability rather than the device type.
 *
 * A device with both touch and mouse (like a Surface) will report based on the
 * primary input method, which is the behavior we want for picker interactions.
 */
const TOUCH_MEDIA_QUERY = '(pointer: coarse)';

/**
 * Hook that detects if the user's primary pointing device is touch-based.
 *
 * Uses CSS `pointer: coarse` media query which is:
 * - More reliable than user-agent sniffing
 * - Detects actual input capability, not device type
 * - Handles hybrid devices (touch laptops) based on primary input
 *
 * Use this hook when deciding between native mobile UI vs custom desktop UI,
 * as it determines whether touch-optimized interactions (like native pickers)
 * would provide a better experience than mouse/keyboard-optimized ones.
 *
 * @returns `true` if the primary pointing device is touch-based (coarse pointer)
 *
 * @example
 * ```tsx
 * const isTouchDevice = useIsTouchDevice();
 *
 * // Show native time picker on touch devices, custom picker on desktop
 * return isTouchDevice ? <NativeTimePicker /> : <CustomTimePicker />;
 * ```
 */
export function useIsTouchDevice(): boolean {
  const [isTouchDevice, setIsTouchDevice] = useState(() => {
    return withWindow(win => win.matchMedia(TOUCH_MEDIA_QUERY).matches, false);
  });

  useEffect(() => {
    return withWindow(win => {
      const mediaQuery = win.matchMedia(TOUCH_MEDIA_QUERY);

      // Update state when media query result changes
      // This handles scenarios like connecting/disconnecting external displays
      // or switching between tablet and laptop modes
      const handleChange = (event: MediaQueryListEvent) => {
        setIsTouchDevice(event.matches);
      };

      // Modern browsers use addEventListener, older ones use addListener
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      } else {
        // Fallback for older browsers (Safari < 14)
        mediaQuery.addListener(handleChange);
      }

      // Sync state in case it changed between render and effect
      setIsTouchDevice(mediaQuery.matches);

      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange);
        } else {
          mediaQuery.removeListener(handleChange);
        }
      };
    });
  }, []);

  return isTouchDevice;
}
